import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import ImageUploading from 'react-images-uploading'
import { getCookiesValue } from 'helpers/cookie'
import {
  ImageUploaderWrap,
  ImageUploaderLabel,
  ImageUploaderPicker,
  ImageUploaderPreview,
} from './styled'

const ImageUploader = ({
  label,
  fileImage,
  onUpload,
  style,
  disabled = false,
  ...otherProps
}) => {
  const [image, setImage] = useState(null)
  const objectUrlRef = useRef(null)

  useEffect(() => {
    let isMounted = true

    const clearObjectUrl = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }

    const prepareImage = async () => {
      if (!fileImage?.length) {
        clearObjectUrl()
        setImage(null)
        return
      }

      const [firstImage] = fileImage

      if (!firstImage?.dataUrl) {
        clearObjectUrl()
        setImage(null)
        return
      }

      if (!firstImage.isApi) {
        clearObjectUrl()
        setImage(fileImage)
        return
      }

      try {
        const token = getCookiesValue('ac_token')

        const response = await fetch(firstImage.dataUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          if (isMounted) {
            clearObjectUrl()
            setImage(null)
          }
          return
        }

        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)

        clearObjectUrl()
        objectUrlRef.current = objectUrl

        if (isMounted) {
          setImage([
            {
              ...firstImage,
              dataUrl: objectUrl,
            },
          ])
        }
      } catch {
        if (isMounted) {
          clearObjectUrl()
          setImage(null)
        }
      }
    }

    prepareImage()

    return () => {
      isMounted = false
    }
  }, [fileImage])

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }
    }
  }, [])

  const onChange = (nextImage) => {
    if (disabled) {
      return
    }

    onUpload(nextImage)
    setImage(nextImage)
  }

  const handleImageUpdate = (index, onImageUpdate) => {
    if (disabled) {
      return
    }

    onImageUpdate(index)
  }

  return (
    <ImageUploaderWrap style={style}>
      {label && <ImageUploaderLabel>{label}</ImageUploaderLabel>}

      <ImageUploading
        value={image}
        onChange={onChange}
        maxNumber={1}
        dataURLKey="dataUrl"
        {...otherProps}
      >
        {({
          imageList,
          onImageUpload,
          onImageUpdate,
          isDragging,
          dragProps,
        }) => (
          <ImageUploaderPicker
            tabIndex={0}
            $disabled={disabled}
            className={classNames({
              'is-dragging': !disabled && isDragging,
            })}
            onClick={disabled || image ? undefined : onImageUpload}
            {...(!disabled ? dragProps : {})}
          >
            {imageList?.map((img, index) => (
              <ImageUploaderPreview
                src={img.dataUrl}
                alt=""
                key={index}
                $disabled={disabled}
                onClick={() => handleImageUpdate(index, onImageUpdate)}
              />
            ))}
          </ImageUploaderPicker>
        )}
      </ImageUploading>
    </ImageUploaderWrap>
  )
}

export default ImageUploader
